import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import type { ChatFileAttachment } from "@/api/chat";
import { ImageLightbox } from "./image-lightbox";

/**
 * How a chat attachment looks, in both places one appears: still in the
 * composer before sending, and on a message after it has been sent.
 *
 * Images render as the picture itself rather than a generic icon, and open
 * full size on click. Everything else keeps the existing file chip.
 *
 * This file replaces two byte-identical copies of the chip that had been
 * declared privately in chat-surface.tsx and agent-chat-tab.tsx.
 */

function sizeLabel(bytes: number): string {
  return `${Math.round(bytes / 1024)}KB`;
}

/** A sent attachment, read back from the message's stored metadata. */
export function FileAttachmentChip({ file }: { file: ChatFileAttachment }) {
  const [zoomed, setZoomed] = useState(false);
  const caption = `${file.name} (${sizeLabel(file.size)})`;

  if (file.mimeType.startsWith("image/")) {
    return (
      <>
        <figure className="max-w-[280px]">
          <button
            type="button"
            onClick={() => setZoomed(true)}
            title={`${caption} - click to zoom`}
            aria-label={`Zoom ${file.name}`}
            className="block overflow-hidden rounded-lg border border-border transition-colors hover:border-accent"
          >
            <img
              src={file.url}
              alt={file.name}
              loading="lazy"
              className="max-h-[200px] w-auto object-contain"
            />
          </button>
          <figcaption className="mt-1 truncate text-[11px] text-text-muted">{caption}</figcaption>
        </figure>
        {zoomed && (
          <ImageLightbox src={file.url} alt={file.name} onClose={() => setZoomed(false)} />
        )}
      </>
    );
  }

  return (
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-hover px-3 py-2 text-sm font-medium hover:bg-surface-active transition-colors"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
        <FileText size={16} />
      </span>
      <span className="max-w-[200px] truncate">{file.name}</span>
      <span className="text-xs opacity-60">({sizeLabel(file.size)})</span>
    </a>
  );
}

/**
 * A picked-but-not-yet-sent image in the composer.
 *
 * The object URL is created once per File and revoked when the file is
 * removed. Building it inline during render minted a fresh URL on every
 * re-render, so the browser refetched the same picture repeatedly and the
 * earlier URLs were never released.
 */
export function PendingImageThumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!url) return <span className="h-10 w-10 shrink-0 rounded bg-surface-active" />;

  return (
    <>
      <button
        type="button"
        onClick={() => setZoomed(true)}
        title={`${file.name} (${sizeLabel(file.size)}) - click to zoom`}
        aria-label={`Zoom ${file.name}`}
        className="h-10 w-10 shrink-0 overflow-hidden rounded border border-border transition-colors hover:border-accent"
      >
        <img src={url} alt={file.name} className="h-full w-full object-cover" />
      </button>
      {zoomed && <ImageLightbox src={url} alt={file.name} onClose={() => setZoomed(false)} />}
    </>
  );
}
